import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';

import {
  ButtonModule,
  ControlValueChangesDirective,
  IconModule,
  Nullable,
  SelectComponent,
  SimpleInputModule,
  SpinnerModule,
  TextAreaComponentModule,
  UploaderModule,
} from '@pinup-teams/common';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from '@fb/environments';

import {
  FinancialCategory,
  GetCoursesReq,
  GetFinancialCategoriesRes,
  GetLocationsRes,
  SupportCategories,
  SupportRequest,
} from '../../models';
import { WithNotRelatedOptionPipe } from '../../pipes/with-not-related-option.pipe';
import { TranslateInPipe } from '../../pipes/translate-in.pipe';

const getForm = (formBuilder: FormBuilder, category: SupportCategories) => {
  return formBuilder.group({
    text: new FormControl('', Validators.required),
    images: new FormControl([]),
    course: new FormControl(null),
    subject: new FormControl(null, category === SupportCategories.education ? Validators.required : null),
    financialCategory: new FormControl(null, category === SupportCategories.finance ? Validators.required : null),
    source: new FormControl(category),
    extraInformation: formBuilder.group({
      location_id: new FormControl(null, category === SupportCategories.office ? Validators.required : null),
      office_space: new FormControl(null),
      office_floor: new FormControl(null),
    }),
  });
};

@Component({
  selector: 'pu-support-form',
  standalone: true,
  imports: [
    OverlayModule,
    ButtonModule,
    IconModule,
    ReactiveFormsModule,
    SimpleInputModule,
    TextAreaComponentModule,
    UploaderModule,
    SelectComponent,
    WithNotRelatedOptionPipe,
    AsyncPipe,
    TranslateModule,
    SpinnerModule,
    TranslateInPipe,
    ControlValueChangesDirective,
  ],
  providers: [],
  templateUrl: './support-form.component.html',
  styleUrl: './support-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportFormComponent implements OnInit, OnChanges {
  readonly #formBuilder = inject(FormBuilder);

  @Input() isLoading: boolean;
  @Input() requestIdHash: string;
  @Input() category: SupportCategories;
  @Input() courses: Nullable<Observable<GetCoursesReq>>;
  @Input() locations: Nullable<Observable<GetLocationsRes>>;
  @Input() financialCategories: Nullable<Observable<GetFinancialCategoriesRes>>;
  @Output() closeDialog = new EventEmitter<NonNullable<SupportRequest>>();

  assetsUrl = environment.assetsUrl;
  apiHost = environment.apiHost;
  categoriesEnum = SupportCategories;
  apiS3Url = signal<Nullable<string>>(null);
  selectedFinancialCategoryId = signal<Nullable<number>>(null);

  form: FormGroup;

  get acceptLabelKey(): string {
    return this.category === this.categoriesEnum.finance ? 'supportForm.files.allowed' : null;
  }

  get maxFileSize(): number {
    return this.category === this.categoriesEnum.finance ? 10 * 1024 * 1024 : null;
  }

  get maxFileSizeLabelKey(): string {
    return this.category === this.categoriesEnum.finance ? 'supportForm.files.size' : null;
  }

  ngOnInit(): void {
    this.form = getForm(this.#formBuilder, this.category);
  }

  ngOnChanges({ requestIdHash }: SimpleChanges): void {
    if (requestIdHash?.currentValue && this.category === this.categoriesEnum.finance) {
      this.apiS3Url.set(`financial-requests/${this.requestIdHash}/files/`);
      this.form.controls['financialCategory'].setValidators(Validators.required);
      this.form.controls['financialCategory'].updateValueAndValidity();
    }
  }

  changeFinancialCategoryId(value: Event): void {
    this.selectedFinancialCategoryId.set(+value);
  }

  getDescription(categories: FinancialCategory[]): string {
    return categories?.find(category => category.id === this.selectedFinancialCategoryId())?.description;
  }

  submit(): void {
    this.closeDialog.emit(this.form.getRawValue());
  }

  close(): void {
    this.closeDialog.emit();
  }

  isNotImage(fileName: string) {
    return /\.(pdf|docx|xlsx|doc|xls)$/i.test(fileName);
  }
}
